import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components';
import { FiUsers, FiCalendar, FiClock, FiBookOpen, FiEye } from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';

const TeacherBatches = () => {
  const navigate = useNavigate();
  const { currentColor, currentUser } = useStateContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchDetails, setBatchDetails] = useState({});

  useEffect(() => {
    const fetchTeacherBatches = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const teacherId = localStorage.getItem('user_id');
        
        // Fetch batches assigned to the teacher
        const batchesResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/BatchTeachers/teacher/${teacherId}`);
        
        if (batchesResponse.data && batchesResponse.data.batchTeachers) {
          // Get batch details for each assigned batch
          const batchesData = [];
          const detailsObj = {};
          
          for (const batchTeacher of batchesResponse.data.batchTeachers) {
            try {
              // Get batch details
              const batchResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/${batchTeacher.batch_id}`);
              
              if (batchResponse.data) {
                batchesData.push(batchResponse.data);
                
                // Get content details for this batch
                const contentResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${batchResponse.data.content_id}`);
                
                // Get student count
                const studentCountResponse = await axios.get(
                  `https://apissc.sainikschoolcadet.com/api/BatchStudents/batch/${batchTeacher.batch_id}/count`
                );
                
                detailsObj[batchTeacher.batch_id] = {
                  content: contentResponse.data,
                  studentCount: studentCountResponse.data.student_count,
                  assignedAt: batchTeacher.assigned_at
                };
              }
            } catch (batchErr) {
              console.error(`Error fetching details for batch ${batchTeacher.batch_id}:`, batchErr);
            }
          }
          
          setBatches(batchesData);
          setBatchDetails(detailsObj);
        }
      } catch (err) {
        console.error('Error fetching teacher batches:', err);
        setError('Failed to load your assigned batches. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherBatches();
  }, [currentUser]);

  const handleViewBatch = (batchId) => {
    navigate(`/BatchView/${batchId}`);
  };

  // If loading
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Teacher" title="My Batches" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading your batches...</p>
          </div>
        </div>
      </div>
    );
  }

  // If error
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category="Teacher" title="My Batches" />
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-8">
        <Header category="Teacher" title="My Classes" />
        <p className="text-gray-600 mt-2">View and manage your assigned class batches</p>
      </div>
      
      {/* Batch List */}
      {batches.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-gray-200 text-gray-400">
              <FiCalendar size={24} />
            </div>
          </div>
          <p className="text-gray-600 mb-4">You don't have any batches assigned to you yet.</p>
          <p className="text-gray-500 text-sm">Batches will appear here once they are assigned to you by an administrator.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {batches.map((batch) => {
            const details = batchDetails[batch.batch_id] || {};
            const content = details.content || {};
            
            return (
              <div key={batch.batch_id} className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="bg-gray-50 p-4 border-b">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-semibold text-gray-800">{batch.batch_name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      batch.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Course</h4>
                    <p className="font-medium flex items-center">
                      <FiBookOpen className="mr-2 text-gray-600" />
                      {content.title || 'Course title unavailable'}
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">DETAILS</h4>
                    <p className="text-sm text-gray-600 mb-2 flex items-center">
                      <FiUsers className="inline mr-2" /> 
                      Students: {details.studentCount || 0}
                    </p>
                    <p className="text-sm text-gray-600 mb-2 flex items-center">
                      <FiClock className="inline mr-2" /> 
                      Assigned: {new Date(details.assignedAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {content.description && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">DESCRIPTION</h4>
                      <p className="text-sm text-gray-600">
                        {content.description.length > 100 
                          ? `${content.description.substring(0, 100)}...` 
                          : content.description}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-6">
                    <button 
                      onClick={() => handleViewBatch(batch.batch_id)}
                      className="w-full px-4 py-2 rounded-md text-white text-sm flex items-center justify-center"
                      style={{ backgroundColor: currentColor }}
                    >
                      <FiEye className="mr-2" /> View Batch
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeacherBatches;